/**
 * PGSS-5718: on an existing stock product staff enter the warehouse count (what is on the shelf); the server works
 * out In Stock Qty = count − allocated. A count below what is allocated takes stock back from the newest
 * orders first, and those orders move to Back Order.
 */
import { defaultWording } from './inventoryWording';

const toInt = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

/** True when the Total field is the warehouse count: an existing, non-custom product. */
export function isWarehouseCountProduct(product) {
  return Boolean(product?.id) && !product?.is_custom;
}

/** Units allocated from stock to open orders: committed (ordered) minus short fall (not yet allocated). */
export function allocatedFor(product) {
  return Math.max(0, toInt(product?.committed) - toInt(product?.short_fall));
}

/** What a count would do, for the preview under the field. Returns null when nothing usable was typed. */
export function warehouseCountPreview(count, product) {
  if (count === '' || count === null || count === undefined || !Number.isFinite(Number(count))) {
    return null;
  }
  const value = toInt(count);
  const allocated = allocatedFor(product);
  return {
    count: value,
    allocated,
    available: Math.max(0, value - allocated),
    released: Math.max(0, allocated - value),
  };
}

/** PGSS-5724: the hint in the wording set in Settings; `w` from useInventoryWording, today's wording without it. */
export function warehouseCountHelperText(preview, w = defaultWording) {
  if (!preview) return undefined;
  if (preview.count < 0) return w('warehouseCountNegativeHint');
  if (preview.released > 0) {
    return w('warehouseCountBelowHint', preview);
  }
  return w('warehouseCountHint', preview);
}

/** Adds warehouse_count to the save payload only when staff changed the count on an existing stock product. */
export function withWarehouseCount(formData, dirtyFields) {
  if (!isWarehouseCountProduct(formData) || !dirtyFields?.total_quantity) {
    return formData;
  }
  const count = formData.total_quantity;
  if (count === '' || count === null || count === undefined) {
    return formData;
  }
  return { ...formData, warehouse_count: String(toInt(count)) };
}
